import { Grid } from '@mui/material';
import { Location, TRANSPORT_MODES, TransportModes } from '@nx-smartmonkey/shared/domain';
import { validateEmail } from '@nx-smartmonkey/shared/helpers';
import { Checkbox, HSpacer, RoutalDropdownIcon, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { RoutalPlaceIcon } from '../../../../assets/svg/RoutalPlaceIcon';
import { ModalSelect } from '../../../../components/ModalSelect';
import { SelectPill } from '../../../../components/SelectPill';
import { getVehicleTypeIcon } from '../../../../components/VehicleTypeIcon';
import { fuelConsumptionUnitsMap, FuelTypes } from '../../../../domain/vehicle/Vehicle';
import { intl } from '../../../../helpers/IntGlobalProvider';
import InputText from '../../../../react-components/InputText';
import { RoutalTimeWindowInput } from '../../../../react-components/InputTimeWindow';
import { Text } from '../../../../react-components/Text';
import { usePermissions } from '../../hooks/usePermissions';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { PRIVATE_COMPONENT_ID } from '../../PrivateComponent';
import { EditStopAddress } from '../../route/stops/create-stops/bulk/EditStopAddress';
import { ValidatedAddress } from '../../route/stops/create-stops/bulk/ValidateStopsBulk';
import { useUpdateProject } from '../../routes-list/hooks/useUpdateProject';
import { useUpdateVehicle } from '../../routes-list/hooks/useUpdateVehicle';

export const VehiclePreferences = () => {
  const { data: vehicleData } = useRetrieveVehicle();
  const { isSoloDriver } = usePermissions();

  const vehicle = vehicleData?.vehicle;
  const { isLoading: loadingVehicle, error: updateError, run: updateVehicle } = useUpdateVehicle();

  const project = vehicleData?.project;
  const { isLoading: loadingPreferences, error: updateProjectError, run: updateProject } = useUpdateProject();

  const [openModalFuel, setOpenModalFuel] = useState(false);
  const [openModalVehicle, setOpenModalVehicle] = useState(false);
  const [openEditOriginAddress, setOpenEditOriginAddress] = useState(false);
  const [openEditDestinationAddress, setOpenEditDestinationAddress] = useState(false);

  const [fuel, setFuel] = useState<FuelTypes | undefined>(vehicle?.fuel_type);
  const [vehicleType, setVehicleType] = useState<TransportModes | undefined>(project?.transport_mode);

  const { circularRouteEnabled, editingAddressOrigin, editingAddressDestination } = useMemo(
    () => ({
      circularRouteEnabled: vehicle?.default_start_location?.equals(vehicle?.default_end_location) || false,
      editingAddressOrigin: {
        rawAddress: vehicle?.default_start_location?.label ?? ``,
        location: vehicle?.default_start_location,
        status: `successful`,
      } as ValidatedAddress,
      editingAddressDestination: {
        rawAddress: vehicle?.default_end_location?.label ?? ``,
        location: vehicle?.default_end_location,
        status: `successful`,
      } as ValidatedAddress,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehicleData]
  );

  // Reset modals
  useEffect(() => {
    if (!loadingVehicle && updateError) {
      openModalFuel && setOpenModalFuel(false);
      openEditOriginAddress && setOpenEditOriginAddress(false);
      openEditDestinationAddress && setOpenEditDestinationAddress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingVehicle, updateError]);

  // Reset modals
  useEffect(() => {
    if (!loadingPreferences && updateProjectError) {
      openModalVehicle && setOpenModalVehicle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPreferences, updateProjectError]);

  if (!vehicle) return null;

  const debouncedUpdateEmail = debounce((value: string) => {
    if (vehicle)
      updateVehicle({
        vehicle: vehicleData.vehicle,
        changes: {
          email: value,
        },
      });
  }, 1500);

  const debouncedUpdateConsumption = debounce((value: number) => {
    if (vehicle && value && value >= 0)
      updateVehicle({
        vehicle: vehicleData.vehicle,
        changes: {
          consumption: value,
        },
      });
  }, 1500);

  const manageSelectedFuel = (fuelType: FuelTypes) => {
    if (vehicle)
      updateVehicle({
        vehicle: vehicleData.vehicle,
        changes: {
          fuel_type: fuelType,
        },
      });
  };

  const manageSelectedVehicleType = (transport_mode: TransportModes) => {
    if (project)
      updateProject({
        transport_mode,
      });
  };

  return (
    <>
      <Grid
        container
        direction="column"
        style={{
          display: `flex`,
          flexDirection: `column`,
          flexWrap: `nowrap`,
        }}
      >
        <Text variant="label" intlId="driver.stops.config.preferences.personal" style={{ fontSize: `16px` }} />
        <VSpacer medium />
        <Text variant="label" intlId="driver.stops.config.preferences.personal.email" style={{ fontSize: `13px` }} />

        <VSpacer small />
        <InputText
          type="email"
          placeholder={intl.formatMessage({
            id: !isSoloDriver
              ? `driver.stops.config.preferences.personal.email_ph.empty` // This is  because planner drivers cant change the email, so the placeholder is different
              : `driver.stops.config.preferences.personal.email_ph`,
          })}
          disabled={!isSoloDriver || loadingVehicle}
          hoverBGColor={!isSoloDriver ? RoutalPalette.neutral00 : RoutalPalette.neutral02n}
          bGColor={!isSoloDriver ? RoutalPalette.neutral00 : RoutalPalette.neutral02n}
          fieldValue={vehicle.email}
          handleEnter={(value) => debouncedUpdateEmail(value)}
          variant="outlined"
          inputContainerStyles={{
            width: `100%`,
            height: `42px`,
            border: `1px solid ${RoutalPalette.neutral10}`,
          }}
          inputStyles={{
            width: `100%`,
            padding: `16.5px 14px`,
            height: `42px`,
          }}
          validators={[
            {
              errorMessage: (
                <Text
                  tagName="span"
                  style={{ color: RoutalPalette.error.medium }}
                  intlId="driver.stops.config.preferences.personal.invalid_email"
                />
              ),
              isInvalid: (text?: string) => (text ?? ``).length > 5 && !validateEmail(text!),
            },
          ]}
        />

        <VSpacer medium />

        {isSoloDriver && (
          <>
            <Text
              variant="label"
              intlId="driver.stops.config.preferences.personal.time_window"
              style={{ fontSize: `13px` }}
            />
            <VSpacer small />

            <RoutalTimeWindowInput
              timeWindow={vehicle.default_time_window}
              onTimewindowChange={(timeWindow) => {
                console.info(timeWindow);
                if (vehicle && !loadingVehicle)
                  updateVehicle({
                    vehicle: vehicleData.vehicle,
                    changes: {
                      default_time_window: timeWindow,
                    },
                  });
              }}
              timeWindowInputStyle={{
                height: `18px`,
                borderColor: RoutalPalette.neutral10,
                backgroundColor: RoutalPalette.white,
              }}
              timeWindowInputContainerStyle={{
                backgroundColor: RoutalPalette.white,
              }}
            />

            <VSpacer medium />

            <Grid style={{ display: `flex`, flexDirection: `row`, alignItems: `center` }}>
              <Text
                variant="label"
                intlId="driver.stops.config.preferences.personal.departure"
                style={{ fontSize: `13px` }}
              />
              <HSpacer small />
              <Checkbox
                size="medium"
                checked={circularRouteEnabled}
                onChange={() => {
                  if (vehicle) {
                    updateVehicle({
                      vehicle: vehicleData.vehicle,
                      changes: {
                        default_end_location: circularRouteEnabled
                          ? Location.create({})
                          : vehicleData.vehicle.default_start_location,
                      },
                    });
                  }
                }}
                disabled={loadingVehicle}
              />
            </Grid>

            <VSpacer small />
            <SelectPill
              radialIcon={false}
              onClick={() => {
                if (!loadingVehicle) {
                  setOpenEditOriginAddress(true);
                }
              }}
              containerStyle={{
                borderColor: RoutalPalette.neutral10,
                alignItems: `center`,
                cursor: `pointer`,
              }}
            >
              <RoutalPlaceIcon color={RoutalPalette.secondary80} />
              <HSpacer small />
              <Text>{editingAddressOrigin?.rawAddress}</Text>
            </SelectPill>
            <VSpacer medium />
            {!circularRouteEnabled && (
              <>
                <Text
                  variant="label"
                  intlId="driver.stops.config.preferences.personal.arrival"
                  style={{ fontSize: `13px` }}
                />
                <VSpacer small />
                <SelectPill
                  radialIcon={false}
                  onClick={() => {
                    if (!loadingVehicle) {
                      setOpenEditDestinationAddress(true);
                    }
                  }}
                  containerStyle={{
                    borderColor: RoutalPalette.neutral10,
                    alignItems: `center`,
                    cursor: `pointer`,
                  }}
                >
                  <RoutalPlaceIcon color={RoutalPalette.secondary80} />
                  <HSpacer small />
                  <Text>{editingAddressDestination?.rawAddress}</Text>
                </SelectPill>
                <VSpacer medium />
              </>
            )}

            <Text variant="label" intlId="driver.stops.config.preferences.vehicle_type" style={{ fontSize: `13px` }} />

            <VSpacer small />
            <SelectPill
              radialIcon={false}
              onClick={() => {
                if (!loadingPreferences) setOpenModalVehicle(true);
              }}
              containerStyle={{
                borderColor: RoutalPalette.neutral10,
                justifyContent: `space-between`,
                alignItems: `center`,
                cursor: `pointer`,
                backgroundColor: RoutalPalette.neutral02n,
              }}
            >
              <Grid container direction="row" gap="8px" alignItems="center">
                {vehicleType !== undefined && <img src={getVehicleTypeIcon(vehicleType)} alt="" />}
                <Text
                  style={{ color: RoutalPalette.primary80 }}
                  intlId={`driver.stops.config.preferences.vehicle_type.${vehicleType ?? `empty`}`}
                />
              </Grid>
              <RoutalDropdownIcon style={{ transform: `rotate(-90deg)` }} />
            </SelectPill>
            <VSpacer medium />
          </>
        )}

        <Text variant="label" intlId="driver.stops.config.preferences.fuel" style={{ fontSize: `13px` }} />

        <VSpacer small />
        <SelectPill
          radialIcon={false}
          onClick={() => {
            if (isSoloDriver && !loadingVehicle) setOpenModalFuel(true);
          }}
          containerStyle={{
            borderColor: RoutalPalette.neutral10,
            justifyContent: `space-between`,
            alignItems: `center`,
            cursor: `pointer`,
            backgroundColor: !isSoloDriver ? RoutalPalette.neutral00 : RoutalPalette.neutral02n,
          }}
        >
          <Text
            style={{ width: `100%`, color: isSoloDriver ? RoutalPalette.primary80 : RoutalPalette.neutral10 }}
            intlId={`driver.stops.config.preferences.fuel.${fuel ?? `empty`}`}
          />
          {isSoloDriver && <RoutalDropdownIcon style={{ transform: `rotate(-90deg)` }} />}
        </SelectPill>
        {fuel && (
          <>
            <VSpacer medium />
            <Text variant="label" intlId="driver.stops.config.preferences.consumption" style={{ fontSize: `13px` }} />
            <VSpacer small />
            <Grid item>
              <InputText
                fieldValue={vehicle.consumption ?? 0}
                onChange={(value: number) => {
                  debouncedUpdateConsumption(value);
                }}
                disabled={!isSoloDriver || loadingVehicle}
                variant="outlined"
                type="number"
                hoverBGColor={!isSoloDriver ? RoutalPalette.neutral00 : RoutalPalette.neutral02n}
                bGColor={!isSoloDriver ? RoutalPalette.neutral00 : RoutalPalette.neutral02n}
                inputContainerStyles={{ width: `100%`, border: `1px solid ${RoutalPalette.neutral10}` }}
                inputStyles={{
                  width: `100%`,
                  padding: `16.5px 14px`,
                }}
                suffix={fuel ? fuelConsumptionUnitsMap[fuel] : undefined} //Consumption suffix based on fuel type
              />
            </Grid>
          </>
        )}
      </Grid>

      <ModalSelect
        open={openModalFuel}
        title={{ intlId: `drivers.preferences.modal.fuel.title` }}
        subtitle={{ intlId: `drivers.preferences.modal.fuel.subtitle` }}
        onClose={() => setOpenModalFuel(false)}
        selectors={[
          {
            selected: fuel === `gasoline`,
            onClick: () => {
              setFuel(`gasoline`);
            },
            children: <Text style={{ cursor: `pointer` }} intlId={`driver.stops.config.preferences.fuel.gasoline`} />,
          },
          {
            selected: fuel === `diesel`,
            onClick: () => {
              setFuel(`diesel`);
            },
            children: <Text style={{ cursor: `pointer` }} intlId={`driver.stops.config.preferences.fuel.diesel`} />,
          },
          {
            selected: fuel === `electric`,
            onClick: () => {
              setFuel(`electric`);
            },
            children: <Text style={{ cursor: `pointer` }} intlId={`driver.stops.config.preferences.fuel.electric`} />,
          },
        ]}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              if (fuel) manageSelectedFuel(fuel);
              setOpenModalFuel(false);
            },
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            variant: `text`,
            onClick: () => {
              setFuel(vehicle?.fuel_type);
              setOpenModalFuel(false);
            },
            style: {
              border: `none`,
            },
          },
        ]}
      />

      <ModalSelect
        open={openModalVehicle}
        title={{ intlId: `drivers.preferences.modal.vehicle_type.title` }}
        subtitle={{ intlId: `drivers.preferences.modal.vehicle_type.subtitle` }}
        onClose={() => setOpenModalVehicle(false)}
        selectors={TRANSPORT_MODES.map((transportMode) => ({
          selected: vehicleType === transportMode,
          onClick: () => {
            setVehicleType(transportMode);
          },
          children: (
            <Grid container direction="row" gap="8px" alignItems="center">
              <img src={getVehicleTypeIcon(transportMode)} alt="" />
              <Text
                style={{ cursor: `pointer` }}
                intlId={`driver.stops.config.preferences.vehicle_type.${transportMode}`}
              />
            </Grid>
          ),
        }))}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              if (vehicleType) manageSelectedVehicleType(vehicleType);
              setOpenModalVehicle(false);
            },
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            variant: `text`,
            onClick: () => {
              setVehicleType(project?.transport_mode);
              setOpenModalVehicle(false);
            },
            style: {
              border: `none`,
            },
          },
        ]}
      />

      {openEditOriginAddress && (
        <EditStopAddress
          isOpen={openEditOriginAddress}
          initialValidatedAddress={editingAddressOrigin}
          onUpdate={(newValidatedAddress) => {
            if (vehicle) {
              if (!circularRouteEnabled) {
                updateVehicle({
                  vehicle: vehicleData.vehicle,
                  changes: {
                    default_start_location: newValidatedAddress.location,
                  },
                });
              } else {
                updateVehicle({
                  vehicle: vehicleData.vehicle,
                  changes: {
                    default_start_location: newValidatedAddress.location,
                    default_end_location: newValidatedAddress.location,
                  },
                });
              }
            }
            setOpenEditOriginAddress(false);
          }}
          onClose={() => setOpenEditOriginAddress(false)}
          portalElementId={PRIVATE_COMPONENT_ID}
        />
      )}
      {openEditDestinationAddress && (
        <EditStopAddress
          isOpen={openEditDestinationAddress}
          initialValidatedAddress={editingAddressDestination}
          onUpdate={(newValidatedAddress) => {
            if (vehicle) {
              updateVehicle({
                vehicle: vehicleData.vehicle,
                changes: {
                  default_end_location: newValidatedAddress.location,
                },
              });
            }
            setOpenEditDestinationAddress(false);
          }}
          onClose={() => setOpenEditDestinationAddress(false)}
          portalElementId={PRIVATE_COMPONENT_ID}
        />
      )}
    </>
  );
};
